import { ArrowRight, Check, FlaskConical, LockKeyhole, RotateCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { chapters } from "../content/journey";
import type { ChapterId, StoredProgress } from "../types";
import { preloadChapterStageAssets, warmStageForJourneyMap } from "../scene/stagePreload";
import { AssetImage } from "./AssetImage";

interface JourneyMapProps {
  progress: StoredProgress;
  onOpenChapter: (id: ChapterId) => void;
  onOpenQuiz: () => void;
  completionNotice?: {
    chapterId: ChapterId;
    message: string;
  } | null;
}

// 旅程地图按顺序推荐章节，已完成章节始终允许回看。
export function JourneyMap({ progress, onOpenChapter, onOpenQuiz, completionNotice }: JourneyMapProps) {
  const completedSet = new Set(progress.completedChapters);
  const allComplete = chapters.every((chapter) => completedSet.has(chapter.id));
  const [explainedChapter, setExplainedChapter] = useState<ChapterId | null>(null);
  const [quizExplanationVisible, setQuizExplanationVisible] = useState(false);
  const completionRef = useRef<HTMLDivElement>(null);
  const chapterRefs = useRef<Partial<Record<ChapterId, HTMLButtonElement>>>({});

  useEffect(() => {
    warmStageForJourneyMap(progress);
  }, [progress]);

  useEffect(() => {
    if (!completionNotice) return;
    const frame = window.requestAnimationFrame(() => {
      const completedIndex = chapters.findIndex((chapter) => chapter.id === completionNotice.chapterId);
      const nextChapter = chapters[completedIndex + 1];
      const focusTarget = nextChapter ? chapterRefs.current[nextChapter.id] : completionRef.current;
      focusTarget?.focus({ preventScroll: true });
      completionRef.current?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        block: "nearest"
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [completionNotice]);

  return (
    <main className="journey-map">
      <AssetImage assetId="1C-01" className="journey-map__art" alt="四章物候旅程地图" sizes="100vw" />
      <div className="journey-map__intro">
        <span className="section-kicker">物候观察旅程</span>
        <h1>四次观察，读懂一年的回信</h1>
        <p>我能看见花与鸟，却还不会读懂它们。请带着我从现象走向证据。</p>
      </div>
      {completionNotice && <div
        className="journey-map__completion"
        role="status"
        aria-live="polite"
        data-completed-chapter={completionNotice.chapterId}
        ref={completionRef}
        tabIndex={-1}
      >
        <Check size={22} aria-hidden="true" />
        <span><strong>章节记录已保存</strong>{completionNotice.message}</span>
      </div>}
      <div className="journey-path">
        {chapters.map((chapter, index) => {
          const complete = completedSet.has(chapter.id);
          const prerequisite = index > 0 ? chapters[index - 1] : undefined;
          const locked = !complete && Boolean(prerequisite && !completedSet.has(prerequisite.id));
          const unlockReason = prerequisite
            ? `请先完成“${prerequisite.title}”，再进入本章。`
            : "当前章节尚未开放。";
          const explaining = explainedChapter === chapter.id;
          return (
            <button
              className={`journey-stop ${complete ? "is-complete" : ""} ${completionNotice?.chapterId === chapter.id ? "is-newly-complete" : ""} ${locked ? "is-locked" : ""} ${explaining ? "is-explained" : ""}`}
              type="button"
              key={chapter.id}
              ref={(node) => {
                chapterRefs.current[chapter.id] = node ?? undefined;
              }}
              aria-label={locked
                ? `${chapter.title}（已锁定）`
                : complete
                  ? `${chapter.title}（已完成，可回看）`
                  : `${chapter.title}（可进入）`}
              aria-disabled={locked || undefined}
              aria-describedby={explaining ? `${chapter.id}-unlock-reason` : undefined}
              onPointerEnter={() => {
                if (!locked) preloadChapterStageAssets(chapter.id);
              }}
              onClick={() => {
                if (locked) {
                  setExplainedChapter(chapter.id);
                  return;
                }
                setExplainedChapter(null);
                onOpenChapter(chapter.id);
              }}
              title={complete ? "已完成，点击回看本章" : locked ? unlockReason : `进入“${chapter.title}”`}
              data-completion-state={complete ? "complete" : locked ? "locked" : "available"}
              style={{ "--chapter-accent": chapter.accent } as React.CSSProperties}
            >
              <span className="journey-stop__number">{complete ? <Check size={19} /> : locked ? <LockKeyhole size={17} /> : chapter.number}</span>
              <span className="journey-stop__text">
                <strong>{chapter.title}</strong>
                <small>{chapter.subtitle}</small>
                {complete && <span className="journey-stop__status"><Check size={14} /> 已完成 · 可随时回看</span>}
                {explaining && <small id={`${chapter.id}-unlock-reason`} className="journey-stop__unlock-reason" role="status">
                  {unlockReason}
                </small>}
              </span>
              {!locked && (complete ? <RotateCcw className="journey-stop__action-icon" size={18} /> : <ArrowRight className="journey-stop__action-icon" size={18} />)}
            </button>
          );
        })}
      </div>
      <div className="journey-map__footer">
        <div className="journey-map__footer-progress">
          <span>{completedSet.size}/4 章已完成</span>
          {!allComplete && (
            <small id="quiz-unlock-reason" className={quizExplanationVisible ? undefined : "sr-only"} role={quizExplanationVisible ? "status" : undefined}>
              还需完成 {chapters.length - completedSet.size} 章，结课核验才会开放。
            </small>
          )}
        </div>
        <button
          className="command-button"
          type="button"
          aria-disabled={!allComplete || undefined}
          aria-describedby={!allComplete ? "quiz-unlock-reason" : undefined}
          data-progress-ready={allComplete ? "true" : "false"}
          onClick={() => {
            if (!allComplete) {
              setQuizExplanationVisible(true);
              return;
            }
            onOpenQuiz();
          }}
        >
          <FlaskConical size={18} /> {allComplete ? "进入结课核验" : "完成四章后解锁"}
        </button>
      </div>
    </main>
  );
}
